import { useRef, useEffect, useMemo } from "react"
import { Control, useController } from "react-hook-form"
import { Popover } from "react-tiny-popover"
import ChevronDownFill from "~icons/eva/chevron-down-fill"
import styles from "./styles.module.scss"
import classNames from "classnames"
import { makeAutoObservable } from "mobx"
import { observer } from "mobx-react-lite"
import { DefaultInput } from "../DefaultInput"
import ChevronUpFill from "~icons/eva/chevron-up-fill"

export type SelectOption<T extends string = string> = { value: T; label: string }
export type OptionsArray<T extends string = string> = SelectOption<T>[]

interface FormSelectInputProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  name: string
  options: OptionsArray
  value?: string | null
  defaultValue?: string | null
  label: string
  parentBackgroundColor: string
  lightOutline?: boolean
  testID?: string
  maxPopoverHeight?: number
  handleChange?: (value?: string) => void
  darkTheme?: boolean
}

class FormSelectInputStore {
  isPopoverOpen: boolean
  containerWidth: number | null
  highlightedIndex: number

  constructor() {
    this.isPopoverOpen = false
    this.containerWidth = null
    this.highlightedIndex = -1

    makeAutoObservable(this)
  }

  setIsPopoverOpen(isOpen: boolean) {
    this.isPopoverOpen = isOpen
    if (isOpen) {
      this.highlightedIndex = -1
    }
  }

  setContainerWidth(width: number) {
    this.containerWidth = width
  }

  setHighlightedIndex(index: number) {
    this.highlightedIndex = index
  }

  moveHighlight(direction: "up" | "down", optionsLength: number) {
    if (direction === "down") {
      this.highlightedIndex = (this.highlightedIndex + 1) % optionsLength
    } else {
      this.highlightedIndex = (this.highlightedIndex - 1 + optionsLength) % optionsLength
    }
  }
}

export const FormSelectInput = observer(
  ({
    control,
    name,
    options = [],
    handleChange,
    testID,
    label,
    parentBackgroundColor,
    lightOutline,
    maxPopoverHeight = 300,
    defaultValue,
    darkTheme = false,
  }: FormSelectInputProps) => {
    const store = useMemo(() => new FormSelectInputStore(), [])
    const ref = useRef<HTMLDivElement>(null)
    const optionsRef = useRef<HTMLDivElement>(null)
    const {
      field: { onChange, value },
    } = useController({ name, control })

    useEffect(() => {
      if (ref.current) {
        store.setContainerWidth(ref.current.offsetWidth)
      }
    }, [ref.current])

    useEffect(() => {
      if (store.isPopoverOpen && optionsRef.current) {
        const highlightedOption = optionsRef.current.querySelector(
          `#option-${store.highlightedIndex}`,
        )
        if (highlightedOption) {
          highlightedOption.scrollIntoView({ block: "nearest" })
        }
      }
    }, [store.highlightedIndex, store.isPopoverOpen])

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!store.isPopoverOpen) {
          if (e.key === "Enter" && document.activeElement === ref.current?.querySelector("input")) {
            e.preventDefault()
            store.setIsPopoverOpen(true)
            return
          }
          return
        }

        switch (e.key) {
          case "ArrowDown":
            e.preventDefault()
            store.moveHighlight("down", options.length)
            break
          case "ArrowUp":
            e.preventDefault()
            store.moveHighlight("up", options.length)
            break
          case "Enter":
            e.preventDefault()
            if (options[store.highlightedIndex]) {
              handleSelectChange(options[store.highlightedIndex])
            }
            break
          case "Escape":
            e.preventDefault()
            store.setIsPopoverOpen(false)
            break
          case "Tab":
            if (store.isPopoverOpen) {
              e.preventDefault()
            }
            break
        }
      }

      document.addEventListener("keydown", handleKeyDown)
      return () => {
        document.removeEventListener("keydown", handleKeyDown)
      }
    }, [options, store.isPopoverOpen])

    const selectedValue = options.find((option: SelectOption) => option.value === value)
    const defaultSelectedValue = options.find(
      (option: SelectOption) => option.value === defaultValue,
    )

    const handleSelectChange = (selectedOption: SelectOption | null) => {
      onChange(selectedOption ? selectedOption.value : "")
      if (handleChange) handleChange(selectedOption ? selectedOption.value : "")
      store.setIsPopoverOpen(false)
    }

    const inputLabel = selectedValue?.label || defaultSelectedValue?.label || ""

    return (
      <Popover
        isOpen={store.isPopoverOpen}
        positions={["bottom"]}
        padding={5}
        ref={ref}
        onClickOutside={() => store.setIsPopoverOpen(false)}
        containerClassName={styles.popoverContainer}
        content={
          <div
            className={classNames(
              styles.optionsContainer,
              darkTheme && styles.optionsContainerDarkTheme,
            )}
            style={{ width: store.containerWidth || 300 }}
          >
            <div
              className={classNames(styles.optionList, darkTheme && styles.optionListDarkTheme)}
              ref={optionsRef}
              style={{ maxHeight: maxPopoverHeight }}
            >
              {options.map((option, index) => {
                const isSelected = selectedValue?.value === option.value
                const isHighlighted = store.highlightedIndex === index

                return (
                  <div
                    key={index}
                    id={`option-${index}`}
                    data-testid={`${testID}-option-${index + 1}`}
                    className={classNames(
                      styles.option,
                      isSelected && styles.selectedOption,
                      isHighlighted && styles.highlightedOption,
                    )}
                    onClick={() => handleSelectChange(option)}
                    onMouseEnter={() => store.setHighlightedIndex(index)}
                  >
                    {option.label}
                  </div>
                )
              })}
            </div>
          </div>
        }
      >
        <div className={styles.defaultInputContainer}>
          <DefaultInput
            type="text"
            label={label}
            value={inputLabel}
            onChange={handleChange}
            onClick={() => store.setIsPopoverOpen(!store.isPopoverOpen)}
            testId={testID}
            name={name}
            inputClassName={classNames(
              styles.selectInput,
              store.isPopoverOpen && styles.selectInputOpen,
            )}
            parentBackgroundColor={parentBackgroundColor}
            lightOutline={lightOutline}
            readOnly
            isModalOpen={store.isPopoverOpen}
            iconRight={
              store.isPopoverOpen ? (
                <ChevronUpFill className={styles.arrow} />
              ) : (
                <ChevronDownFill className={styles.arrow} />
              )
            }
          />
        </div>
      </Popover>
    )
  },
)
